import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatWithSystemLocale } from '../../utils/dateUtils';
import CurrencyInput from "../../components/CurrencyInput";
import FormattedCurrency from "../../components/FormattedCurrency";

import { useNotification } from "../../contexts/NotificationContext";
import { Hammer } from "lucide-react";
import { getApiUrl } from "../../config/api";

export default function Farrier() {
  const { id: horseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [records, setRecords] = useState([]);
  const [farriers, setFarriers] = useState([]);
  const [form, setForm] = useState({
    service_date: "",
    services_performed: "",
    farrier_name: "",
    notes: "",
    amount: ""
  });

  useEffect(() => {
    fetchRecords();
    fetchFarriers();
  }, []);

  // Check for edit parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    if (editId && records.length > 0) {
      const recordToEdit = records.find(rec => rec.id.toString() === editId);
      if (recordToEdit) {
        startEdit(recordToEdit);
        // Clean up URL by removing the edit parameter
        navigate(`/horses/${horseId}/farrier`, { replace: true });
      }
    }
  }, [records, location.search, navigate, horseId]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${horseId}/farrier`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const fetchFarriers = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/farriers`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFarriers(res.data);
    } catch (err) {
      console.error("Error fetching farriers:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFarrierChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, farrier_name: value });
  };

  const handleSubmit = async () => {
    try {
      // Ensure amount defaults to 0 if empty
      const formData = {
        ...form,
        amount: form.amount || "0.00"
      };
      
      await axios.post(getApiUrl(`/api/horses/${horseId}/farrier`), formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setForm({ service_date: "", services_performed: "", farrier_name: "", notes: "", amount: "" });
      fetchRecords();
      showSuccess("Farrier record added successfully!");
    } catch (err) {
      console.error("Submit error:", err);
      showError(err.response?.data?.error || "Failed to add farrier record. Please try again.");
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const goBack = () => navigate("/horses");

 const startEdit = (v) => {
  setEditingId(v.id);
  setEditData({
    service_date: v.service_date ? v.service_date.split("T")[0] : "",
    services_performed: v.services_performed || "",
    farrier_name: v.farrier_name || "",
    notes: v.notes || "",
    amount: v.Amount || ""
  });
};

  const saveEdit = async (id) => {
    try {
      // Ensure amount defaults to 0 if empty
      const editFormData = {
        ...editData,
        amount: editData.amount || "0.00"
      };
      
      await axios.put(getApiUrl(`/api/horses/${horseId}/farrier/${id}`), editFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setEditingId(null);
      fetchRecords();
      showSuccess("Farrier record updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      showError(err.response?.data?.error || "Failed to update farrier record. Please try again.");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(getApiUrl(`/api/farrier/${recordId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      fetchRecords();
      showSuccess("Farrier record deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      showError(err.response?.data?.error || "Failed to delete farrier record. Please try again.");
    }
  };

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      <button
        onClick={goBack}
        className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
      >
  ← Back to Horses
</button>
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Hammer className="mr-2 text-orange-600" /> Farrier
      </h2>

 {!editingId && (
   <div className="bg-white shadow p-6 rounded mb-8">
     <div className="flex items-center justify-between mb-4">
       <h3 className="text-lg font-semibold">Add Farrier Record</h3>
       <button
         onClick={() => setShowForm(!showForm)}
         className="text-sm text-blue-600 hover:underline focus:outline-none"
       >
         {showForm ? "Hide" : "Add"}
       </button>
     </div>

     {showForm && (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Service Date *</label>
        <input
          type="date"
          name="service_date"
          value={form.service_date}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
        <p className="text-xs text-gray-500 mt-1">When the farrier work was performed</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Services Performed *</label>
        <input
          name="services_performed"
          placeholder="e.g., Trim, Shoe, Reset, Pull shoes"
          value={form.services_performed}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
        <p className="text-xs text-gray-500 mt-1">What work was done on the hooves</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Farrier Name</label>
        <div className="relative">
          <input
            list="farriers-list"
            name="farrier_name"
            placeholder="Select from list or type a name"
            value={form.farrier_name}
            onChange={handleFarrierChange}
            className="border px-3 py-2 rounded w-full pr-8"
          />
          <datalist id="farriers-list">
            {farriers.map((farrier) => (
              <option key={farrier.id} value={farrier.name} />
            ))}
          </datalist>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">Select from existing farriers or type a new name</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
        <CurrencyInput
          name="amount"
          value={form.amount}
          onChange={(val) => setForm({ ...form, amount: val })}
          locale={navigator.language || 'en-US'}
          currency="USD"
          className="border px-3 py-2 rounded w-full"
          placeholder="0.00"
        />
        <p className="text-xs text-gray-500 mt-1">Cost of farrier services</p>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <input
          name="notes"
          placeholder="Any additional information about the work performed"
          value={form.notes}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
        <p className="text-xs text-gray-500 mt-1">Additional details or observations</p>
      </div>
    </div>
  )}

       {showForm && (
         <button
           onClick={handleSubmit}
           className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
         >
           Save Farrier Record
         </button>
       )}
     </div>
   )}


      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">History</h3>
        {records.length === 0 ? (
          <p className="text-gray-500">No records yet.</p>
        ) : (
        <ul className="divide-y divide-gray-200">
          {records.map((rec) => (
            <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              {editingId === rec.id ? (
                <div className="w-full sm:flex-grow">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Date *</label>
                    <input
                      type="date"
                      className="border px-2 py-1 rounded w-full"
                      name="service_date"
                      value={editData.service_date}
                      onChange={(e) => setEditData({ ...editData, service_date: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Services Performed *</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="services_performed"
                      value={editData.services_performed}
                      onChange={(e) => setEditData({ ...editData, services_performed: e.target.value })}
                      placeholder="e.g., Trim, Shoe, Reset, Pull shoes"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Farrier Name</label>
                    <div className="relative">
                      <input
                        list="farriers-edit-list"
                        className="border px-2 py-1 rounded w-full"
                        name="farrier_name"
                        value={editData.farrier_name}
                        onChange={(e) => setEditData({ ...editData, farrier_name: e.target.value })}
                        placeholder="Select from list or type a name"
                      />
                      <datalist id="farriers-edit-list">
                        {farriers.map((farrier) => (
                          <option key={farrier.id} value={farrier.name} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="notes"
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      placeholder="Any additional information about the work performed"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                    <CurrencyInput
                      name="amount"
                      value={editData.amount}
                      onChange={(val) => setEditData({ ...editData, amount: val })}
                      locale={navigator.language || 'en-US'}
                      currency="USD"
                      className="border px-2 py-1 rounded w-full"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(rec.id)}
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-300 text-black px-4 py-1 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">{rec.services_performed}</p>
                    <p className="text-sm text-gray-600">
                      Service Date: {formatWithSystemLocale(rec.service_date)}
                    </p>
                    {rec.farrier_name && (
                      <p className="text-sm text-gray-600">
                        Farrier: {rec.farrier_name}
                      </p>
                    )}
                    {rec.notes && (
                      <p className="text-sm text-gray-500 italic">Notes: {rec.notes}</p>
                    )}
                    {rec.Amount && (
                      <p className="text-sm text-gray-500 italic">
                        Cost: <FormattedCurrency value={rec.Amount} />
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(rec)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        )}
      </div>
    </div>
  );
}
